/**
 * VoluntaryExitDialogV2 — Multi-step voluntary exit dialog (V2 redesign).
 *
 * Replicates V1 VoluntaryExitModal 3-step flow with shadcn/Tailwind:
 * Step 1: Warning with 3 info cards
 * Step 2: Financial simulator with contribution calculations
 * Step 3: API call (collectNoWill)
 */
import React, { useState, useMemo } from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Info, Lock, AlertTriangle, Loader2 } from 'lucide-react';
import PensionSimulatorV2 from './SimulatorsV2/PensionSimulatorV2';
import collectNoWill from '../api/users/collectWillOfNo';
import { CurrentUserI } from '../api/users/interfaces/Users';
import { CompanyEppConfigI } from '../api/companies/interfaces/CompanyEppConfig';
import { ContributionTypeEnum } from '../api/users/interfaces/ContributionData';
import { resolveEmployerContribution } from '../utils/resolveEmployerContribution';
import { formatArcaNumber } from '../utils/formatArcaNumbers';
import { resolveCompanyName } from '../utils/resolveCompanyName';

interface VoluntaryExitDialogV2Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentUser: CurrentUserI | null;
	companyEppConfig: CompanyEppConfigI | null;
	token: string;
	translate: (key: string) => string;
	onSuccess: () => void;
}

const VoluntaryExitDialogV2: React.FC<VoluntaryExitDialogV2Props> = ({
	open,
	onOpenChange,
	currentUser,
	companyEppConfig,
	token,
	translate,
	onSuccess
}) => {
	const [step, setStep] = useState<1 | 2 | 3>(1);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const companyName = resolveCompanyName(currentUser, 'la empresa');

	// Reset state when dialog opens/closes
	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setStep(1);
			setError(null);
		}
		onOpenChange(newOpen);
	};

	// --- Contribution calculations (replica V1 L129-228) ---

	const infoContributionsEmployee = useMemo(() => {
		if (currentUser?.userEppConfig?.data?.employeeContributionType === ContributionTypeEnum.FIXED) {
			return Number(currentUser?.userEppConfig?.data?.pensionContribution);
		} else if (
			currentUser?.userEppConfig?.data?.employeeContributionType === ContributionTypeEnum.PERCENTAGE
		) {
			return (
				Number((currentUser?.user?.grossAnnualSalary ?? 0) / 100 / 12) *
				(currentUser?.userEppConfig?.data?.pensionContribution ?? 0)
			);
		}
		return 0;
	}, [
		currentUser?.userEppConfig?.data?.employeeContributionType,
		currentUser?.userEppConfig?.data?.pensionContribution,
		currentUser?.user?.grossAnnualSalary
	]);

	const infoContributionsCompany = useMemo(() => {
		return resolveEmployerContribution({
			calculatedEmployerContribution: undefined,
			companyEppConfig,
			employeeCategory: currentUser?.userEppConfig?.data?.employeeCategory,
			grossAnnualSalary: currentUser?.user?.grossAnnualSalary,
			employmentStartDate: currentUser?.user?.employmentStartDate,
			// MATCHING mode: the company matches the employee's saved contribution
			employeeMonthlyContribution: infoContributionsEmployee
		});
	}, [companyEppConfig, currentUser, infoContributionsEmployee]);

	const employeeAnnual = useMemo(() => infoContributionsEmployee * 12, [infoContributionsEmployee]);
	const companyAnnual = useMemo(() => infoContributionsCompany * 12, [infoContributionsCompany]);

	const roi = useMemo(() => {
		if (employeeAnnual === 0) return companyAnnual > 0 ? Infinity : 0;
		return (companyAnnual + employeeAnnual) / employeeAnnual - 1;
	}, [companyAnnual, employeeAnnual]);

	// --- Contribution text (replica V1 L230-284) ---

	const contributionText = useMemo(() => {
		if (companyEppConfig?.employeeContributionRequired === false) {
			return (
				<p className="text-base text-foreground text-justify">
					<span className="font-bold">{translate('settings.simulator.description.part1')}</span>
					{translate('settings.simulator.description.part2')}
					<span className="font-bold">{formatArcaNumber(companyAnnual)}&euro;</span>
					{translate('settings.simulator.description.part3')}
					<span className="font-bold">{translate('settings.simulator.description.part4')}</span>
					{translate('settings.simulator.description.part5')}
					<span className="font-bold">{translate('settings.simulator.description.part6')}</span>
					{translate('settings.simulator.description.part7')}
					<span className="font-bold">{translate('settings.simulator.description.part8')}</span>
					{translate('settings.simulator.description.part9')}
				</p>
			);
		}
		return (
			<p className="text-base text-foreground text-justify">
				<span className="font-bold">{translate('settings.simulator.description.paid1')}</span>
				<span className="font-bold">{formatArcaNumber(employeeAnnual)}&euro;</span>
				{translate('settings.simulator.description.paid2')}
				<span className="font-bold">{formatArcaNumber(companyAnnual + employeeAnnual)}&euro;</span>
				{translate('settings.simulator.description.paid3')}
				<span className="font-bold">{Number.isFinite(roi) ? formatArcaNumber(roi) : '\u221E'}</span>
				<span className="font-bold">{translate('settings.simulator.description.paid4')}</span>
				{translate('settings.simulator.description.paid5')}
				<span className="font-bold">{translate('settings.simulator.description.paid6')}</span>
				{translate('settings.simulator.description.paid7')}
			</p>
		);
	}, [
		companyEppConfig?.employeeContributionRequired,
		employeeAnnual,
		companyAnnual,
		roi,
		translate
	]);

	// --- Headline text (replica V1 L513-526) ---

	const headlineAmount =
		companyEppConfig?.employeeContributionRequired === false
			? formatArcaNumber(companyAnnual)
			: formatArcaNumber(companyAnnual + employeeAnnual);

	const headlineSuffix =
		companyEppConfig?.employeeContributionRequired === false
			? translate('onboarding.employee.areYouSureMessage2')
			: '';

	// --- API call (step 3, replica V1 L67-110) ---

	const handleConfirmExit = async () => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await collectNoWill(token);
			if (response.success) {
				handleOpenChange(false);
				onSuccess();
			} else {
				setError(translate('settings.voluntaryExit.error.description'));
			}
		} catch {
			setError(translate('settings.voluntaryExit.error.description'));
		} finally {
			setIsLoading(false);
		}
	};

	// --- Info cards for step 1 ---

	const infoCards = [
		{
			icon: Info,
			iconColor: 'text-primary',
			iconBg: 'bg-primary/10',
			titleKey: 'settings.voluntaryExit.instruction3.title',
			descKey: 'settings.voluntaryExit.instruction3.description',
			replacements: {}
		},
		{
			icon: Lock,
			iconColor: 'text-destructive',
			iconBg: 'bg-destructive/10',
			titleKey: 'settings.voluntaryExit.instruction2.title',
			descKey: 'settings.voluntaryExit.instruction2.description',
			replacements: {}
		},
		{
			icon: AlertTriangle,
			iconColor: 'text-amber-600',
			iconBg: 'bg-amber-100',
			titleKey: 'settings.voluntaryExit.instruction1.title',
			descKey: 'settings.voluntaryExit.instruction1.description',
			replacements: { '{companyName}': companyName }
		}
	];

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className={step === 2 ? 'sm:max-w-4xl max-h-[90vh] overflow-y-auto' : 'sm:max-w-lg'}
			>
				{/* ---- STEP 1: Warning ---- */}
				{step === 1 && (
					<>
						<DialogHeader>
							<DialogTitle className="text-center text-primary">
								{translate('settings.voluntaryExit.title').replace('{companyName}', companyName)}
							</DialogTitle>
							<DialogDescription className="text-center text-destructive font-semibold">
								{translate('settings.voluntaryExit.warning')}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-3 py-2">
							{infoCards.map((card) => {
								const Icon = card.icon;
								let desc = translate(card.descKey);
								Object.entries(card.replacements).forEach(([k, v]) => {
									desc = desc.replace(k, v);
								});
								return (
									<div
										key={card.titleKey}
										className="flex items-start gap-3 rounded-lg border border-border p-4"
									>
										<div
											className={`w-8 h-8 rounded-lg ${card.iconBg} flex items-center justify-center shrink-0 mt-0.5`}
										>
											<Icon className={`w-4 h-4 ${card.iconColor}`} />
										</div>
										<div>
											<p className="text-sm font-bold text-foreground">
												{translate(card.titleKey)}
											</p>
											<p className="text-sm text-muted-foreground">{desc}</p>
										</div>
									</div>
								);
							})}
						</div>

						<DialogFooter className="flex gap-2 sm:gap-2">
							<Button
								variant="outline"
								onClick={() => handleOpenChange(false)}
								className="rounded-full"
							>
								{translate('general.genericModal.close')}
							</Button>
							<Button variant="destructive" onClick={() => setStep(2)} className="rounded-full">
								{translate('settings.voluntaryExit.confirm')}
							</Button>
						</DialogFooter>
					</>
				)}

				{/* ---- STEP 2: Financial simulator ---- */}
				{step === 2 && (
					<>
						<DialogHeader>
							<DialogTitle className="text-center text-lg">
								{translate('settings.simulator.title')}
							</DialogTitle>
						</DialogHeader>

						<div className="space-y-4 py-2">
							{contributionText}

							<PensionSimulatorV2 currentAge={0} />

							<div className="text-center">
								<p className="text-lg font-bold">
									{translate('onboarding.employee.areYouSureMessage')}{' '}
									<span className="text-destructive">{headlineAmount}&euro;</span>
									{headlineSuffix && <span className="font-bold">{headlineSuffix}</span>}
								</p>
							</div>

							{error && (
								<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive text-center">
									{error}
								</div>
							)}
						</div>

						<DialogFooter className="flex gap-2 sm:gap-2">
							<Button
								onClick={() => handleOpenChange(false)}
								className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
							>
								{translate('general.genericModal.closeThinkAgain')}
							</Button>
							<Button
								variant="destructive"
								onClick={handleConfirmExit}
								disabled={isLoading}
								className="rounded-full gap-2"
							>
								{isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
								{translate('general.genericModal.confirmCancelAdhesion')}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default VoluntaryExitDialogV2;
